import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import errorMonitoringService from '../../services/errorMonitoringService';
import ErrorDashboardCard from './components/ErrorDashboardCard';
import ErrorLogTable from './components/ErrorLogTable';
import ErrorFilterPanel from './components/ErrorFilterPanel';
import RecoveryWorkflowPanel from './components/RecoveryWorkflowPanel';
import AlertManagementPanel from './components/AlertManagementPanel';

const SystemErrorMonitoringRecoveryCenter = () => {
  const [activeTab, setActiveTab] = useState('error_logs');
  const [errors, setErrors] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [filters, setFilters] = useState({
    errorType: '',
    severity: '',
    errorStatus: '',
    affectedModule: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: ''
  });

  const handleNewError = (newError) => {
    setErrors(prev => [newError, ...prev]);
    loadData();
  };

  useEffect(() => {
    loadData();
    const channel = errorMonitoringService?.subscribeToErrors(handleNewError);
    return () => errorMonitoringService?.unsubscribeFromErrors(channel);
  }, []);

  useEffect(() => {
    loadErrors();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [errorsData, statsData, healthData] = await Promise.all([
        errorMonitoringService?.getAllErrors({ limit: 100 }),
        errorMonitoringService?.getErrorStatistics(),
        errorMonitoringService?.getSystemHealthMetrics()
      ]);
      setErrors(errorsData || []);
      setStatistics(statsData || {});
      setSystemHealth(healthData || {});
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadErrors = async () => {
    try {
      const errorsData = await errorMonitoringService?.getAllErrors(filters);
      setErrors(errorsData || []);
    } catch (error) {
      console.error('Error loading errors:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleErrorSelect = (error) => {
    setSelectedError(error);
  };

  const handleStatusUpdate = async (errorId, status) => {
    try {
      await errorMonitoringService?.updateErrorStatus(errorId, status);
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-600 bg-red-50',
      high: 'text-orange-600 bg-orange-50',
      medium: 'text-yellow-600 bg-yellow-50',
      low: 'text-blue-600 bg-blue-50'
    };
    return colors?.[severity] || 'text-gray-600 bg-gray-50';
  };

  const getHealthStatusColor = (health) => {
    const colors = {
      healthy: 'text-green-600 bg-green-50',
      warning: 'text-yellow-600 bg-yellow-50',
      critical: 'text-red-600 bg-red-50'
    };
    return colors?.[health] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>System Error Monitoring & Recovery Center - PNG HR Analytics</title>
      </Helmet>

      <div className="min-h-screen bg-neutral-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">System Error Monitoring & Recovery Center</h1>
              <p className="text-neutral-600 mt-1">Comprehensive error tracking and automated resolution</p>
            </div>
            <div className="flex items-center gap-3">
              <div className={`px-4 py-2 rounded-lg ${getHealthStatusColor(systemHealth?.systemHealth)}`}>
                <div className="flex items-center gap-2">
                  <Icon name="Activity" size={18} />
                  <span className="font-medium capitalize">{systemHealth?.systemHealth || 'Unknown'}</span>
                </div>
              </div>
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="RefreshCw" size={18} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <ErrorDashboardCard
            title="Active Errors"
            value={statistics?.activeErrors || 0}
            icon="AlertCircle"
            color="red"
            trend={`${statistics?.totalErrors || 0} total`}
          />
          <ErrorDashboardCard
            title="Resolution Rate"
            value={`${statistics?.resolutionRate || 0}%`}
            icon="CheckCircle"
            color="green"
            trend={`${statistics?.resolvedErrors || 0} resolved`}
          />
          <ErrorDashboardCard
            title="Critical Errors"
            value={statistics?.criticalErrors || 0}
            icon="AlertTriangle"
            color="orange"
            trend="Requires immediate attention"
          />
          <ErrorDashboardCard
            title="Auto-Recovered"
            value={statistics?.autoRecoveredErrors || 0}
            icon="Zap"
            color="blue"
            trend={`${((statistics?.autoRecoveredErrors / (statistics?.totalErrors || 1)) * 100)?.toFixed(1)}% success rate`}
          />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Filter Panel */}
          <div className="lg:col-span-3">
            <ErrorFilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              statistics={statistics}
            />
          </div>

          {/* Main Panel */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
              {/* Tabs */}
              <div className="border-b border-neutral-200">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('error_logs')}
                    className={`px-6 py-3 font-medium transition-colors ${
                      activeTab === 'error_logs' ?'text-blue-600 border-b-2 border-blue-600' :'text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="List" size={18} />
                      Error Logs
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('recovery')}
                    className={`px-6 py-3 font-medium transition-colors ${
                      activeTab === 'recovery' ?'text-blue-600 border-b-2 border-blue-600' :'text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="Tool" size={18} />
                      Recovery Workflows
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('alerts')}
                    className={`px-6 py-3 font-medium transition-colors ${
                      activeTab === 'alerts' ?'text-blue-600 border-b-2 border-blue-600' :'text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="Bell" size={18} />
                      Alert Management
                    </div>
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'error_logs' && (
                  <ErrorLogTable
                    errors={errors}
                    onErrorSelect={handleErrorSelect}
                    onStatusUpdate={handleStatusUpdate}
                    selectedError={selectedError}
                  />
                )}
                {activeTab === 'recovery' && (
                  <RecoveryWorkflowPanel
                    selectedError={selectedError}
                    onRefresh={loadData}
                  />
                )}
                {activeTab === 'alerts' && (
                  <AlertManagementPanel
                    errors={errors}
                    statistics={statistics}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SystemErrorMonitoringRecoveryCenter;